import { useMemo } from 'react';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import { VDMS, DSS, CAS, DAS } from '../AuctionSnapshotDetailedView/constants';

const DEFAULT_VDMS_AD_TAGS_PARAMETER_SUMMARY = {
  raw: [],
  hulurequestid: [],
  storedrequestid: [],
  podconfig: [],
  app: [],
  video: [],
  device: [],
  site: [],
  content: [],
  trackedbysupplyintegrator: [],
};

const DEFAULT_DSS_AD_TAGS_PARAMETER_SUMMARY = {
  raw: [],
  app: [],
  video: [],
  device: [],
  site: [],
  trackedbysupplyintegrator: [],
};

const DEFAULT_CAS_AD_TAGS_PARAMETER_SUMMARY = {
  raw: [],
  appinfo: [],
  contentinfo: [],
  deviceinfo: [],
  podinfo: [],
  ruleinfo: [],
  userinfo: [],
  trackedbysupplyintegrator: [],
};

const omitUndefinedValues = (obj) => omitBy(obj, isUndefined);

const extractFilteredFields = (json) => {
  const source = json || {};

  const app = typeof source.app === 'string' ? { name: source.app } : source.app;
  return {
    app,
    device: omitUndefinedValues({
      name: source?.device?.name,
      'ad-id': source?.device?.['ad-id'],
      'ifa-type': source?.device?.['ifa-type'],
      category: source?.device?.category,
      'form-factor': source?.device?.['form-factor'],
      brand: source?.device?.brand,
      'user-agent': source?.device?.['user-agent'],
      'limit-ad-tracking': source?.device?.['limit-ad-tracking'],
    }),
    content: omitUndefinedValues({
      publisher: source?.content?.publisher,
      genres: source?.content?.genres,
      'ratings.value': source?.content?.ratings?.value,
      'playback-type': source?.content?.['playback-type'],
      distributor: source?.content?.distributor,
      ratings: source?.content?.ratings,
    }),
    viewer: omitUndefinedValues({
      'identity-id': source?.viewer?.identity?.['identity-id'],
      'account-id': source?.viewer?.identity?.['account-id'],
      'profile-id': source?.viewer?.identity?.['profile-id'],
    }),
    geo: omitUndefinedValues({
      country: source?.geo?.country,
    }),
  };
};

const vdmsJsonToTagObject = (json) => {
  return {
    raw: json,
    filtered: extractFilteredFields(json),
  };
};

const dssJsonToTagObject = (json) => {
  return {
    raw: json,
    filtered: extractFilteredFields(json),
  };
};

const casJsonToTagObject = (json) => {
  return {
    raw: json,
    filtered: extractFilteredFields(json),
  };
};

const combineTagObjects = (tagObjects) => {
  return tagObjects.reduce((acc, item) => {
    const obj = {};
    for (let key in item) {
      Object.assign(obj, { [key]: [...(acc[key] || []), item[key]] });
    }
    return { ...acc, ...obj };
  }, {});
};

const vdmsAdTagsParameterSummary = (data) => {
  return combineTagObjects(data?.map(({ record }) => vdmsJsonToTagObject(record?.rawData)));
};

const dssAdTagsParameterSummary = (data) => {
  return combineTagObjects(data?.map(({ record }) => dssJsonToTagObject(record?.rawData)));
};

const casAdTagsParameterSummary = (data) => {
  return combineTagObjects(data?.map(({ record }) => casJsonToTagObject(record?.rawData)));
};

const supplySourceMap = {
  [VDMS]: vdmsAdTagsParameterSummary,
  [DSS]: dssAdTagsParameterSummary,
  [CAS]: casAdTagsParameterSummary,
  [DAS]: casAdTagsParameterSummary,
};

export const defaultAdTagsParameterSummaryMap = {
  [VDMS]: DEFAULT_VDMS_AD_TAGS_PARAMETER_SUMMARY,
  [DSS]: DEFAULT_DSS_AD_TAGS_PARAMETER_SUMMARY,
  [CAS]: DEFAULT_CAS_AD_TAGS_PARAMETER_SUMMARY,
  [DAS]: DEFAULT_CAS_AD_TAGS_PARAMETER_SUMMARY,
};

const resolveAdTagsParameterSummary = (type, data) => {
  const fn = supplySourceMap[type];
  return fn ? fn(data) : defaultAdTagsParameterSummaryMap[type];
};

export const useAdTagsParameterSummaryResolver = (type, data) => {
  return useMemo(() => resolveAdTagsParameterSummary(type, data), [type, data]);
};
